import React, { useState, useEffect, useMemo } from 'react';
import { ChevronRight, ChevronDown, Filter, TrendingUp, Search, Calendar, BarChart3, Download } from 'lucide-react';
import { API_URL } from '../config/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, Label } from 'recharts';
import '../styles/subestados-hierarquicos.css';

const SubestadosHierarquicos = ({ startDate, endDate, distributionProportionalConfig, analyticsData, fieldLabels, fieldFormats }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    fetchSubestados();
  }, [startDate, endDate]);

  const fetchSubestados = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const url = `${API_URL}/api/agent-analytics/subestados${params.toString() ? '?' + params.toString() : ''}`;
      const response = await fetch(url, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      setData(result);
      
      // Expandir todos los nodos por defecto
      if (result.hierarchy) {
        const allKeys = result.hierarchy.map(h => h.key);
        setExpandedNodes(new Set(allKeys));
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching subestados:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleNode = (key) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedNodes(newExpanded);
  };

  const expandAll = () => {
    if (data?.hierarchy) {
      const allKeys = data.hierarchy.map(h => h.key);
      setExpandedNodes(new Set(allKeys));
    }
  };

  const collapseAll = () => {
    setExpandedNodes(new Set());
  };

  // Preparar datos para la gráfica - mostrar toda la jerarquía de manera visual
  const chartData = useMemo(() => {
    if (!data?.data) {
      console.log('SubestadosHierarquicos: No hay data.data');
      return { level0: [], level1: [], level2: [], hierarchical: [], all: [] };
    }
    
    const prepareChartData = (nodes, parentName = '', level = 0) => {
      const result = [];
      Object.values(nodes).forEach(node => {
        const fullName = parentName ? `${parentName} > ${node.label}` : node.label;
        const count = node.count || 0;
        const operation = (node.operation || node.op || 'count');
        
        // Agregar el nodo actual
        result.push({
          name: node.label,
          fullName: fullName,
          value: count,
          level: level,
          parent: parentName || null,
          key: node.key,
          operation: String(operation || 'count').toLowerCase().trim()
        });
        
        // Agregar hijos recursivamente
        if (node.children && Object.keys(node.children).length > 0) {
          const childrenData = prepareChartData(node.children, fullName, level + 1);
          result.push(...childrenData);
        }
      });
      return result;
    };
    
    const allData = prepareChartData(data.data);
    // IMPORTANTE: Excluir nodos calculados por SUMA del Top 20 (distorsionan el ranking/porcentajes)
    const allDataNoSum = allData.filter(d => (d.operation || 'count') !== 'sum');
    
    // Preparar datos jerárquicos para gráfico apilado
    const hierarchicalData = [];
    Object.values(data.data).forEach(rootNode => {
      const rootData = {
        name: rootNode.label,
        rootValue: rootNode.count || 0,
        children: []
      };
      
      if (rootNode.children && Object.keys(rootNode.children).length > 0) {
        Object.values(rootNode.children).forEach(child => {
          rootData.children.push({
            name: child.label,
            value: child.count || 0,
            parent: rootNode.label
          });
        });
      }
      
      hierarchicalData.push(rootData);
    });
    
    // Filtrar "gente guía" de los datos de nivel 1 (para el gráfico de desglose detallado)
    const level1Filtered = allData
      .filter(d => d.level === 1)
      .filter(d => {
        // Excluir "gente guía" (case-insensitive)
        const labelLower = (d.name || '').toLowerCase();
        return !labelLower.includes('gente guía') && !labelLower.includes('gente guia') && !labelLower.includes('gente_guia');
      })
      .sort((a, b) => b.value - a.value);
    
    // Preparar datos para distribución proporcional usando campos del paso 4
    let level0Filtered = [];
    
    // Si hay configuración de distribución proporcional con campos del paso 4, usar esos campos
    if (distributionProportionalConfig && Array.isArray(distributionProportionalConfig) && distributionProportionalConfig.length > 0 && analyticsData) {
      // La configuración contiene las keys de los campos del paso 4 que se deben mostrar
      const allowedKeys = new Set(distributionProportionalConfig.map(key => String(key).toLowerCase().trim()));
      
      // Crear datos para la gráfica usando los valores de los campos configurados
      level0Filtered = distributionProportionalConfig
        .map(fieldKey => {
          // Buscar el valor del campo en analyticsData
          const fieldKeyLower = String(fieldKey).toLowerCase().trim();
          let value = 0;
          
          // Obtener el label del campo (usar el configurado o convertir la key a formato legible)
          let label = fieldLabels?.[fieldKey];
          if (!label || label.trim() === '') {
            // Si no hay label configurado, convertir la key a formato legible
            // Ejemplo: "total_calls" -> "Total Calls" o "total_calls_completed" -> "Total Calls Completed"
            label = String(fieldKey)
              .split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ');
          }
          
          // Buscar el valor en analyticsData (puede estar en analyticsData.metrics o analyticsData.m)
          const metricsData = analyticsData.metrics || analyticsData.m || {};
          if (metricsData && typeof metricsData === 'object') {
            // Buscar por key exacto primero
            const exactMatch = metricsData[fieldKey];
            if (exactMatch !== undefined && exactMatch !== null) {
              // Convertir a número, manejando strings formateados
              if (typeof exactMatch === 'string') {
                // Remover comas, porcentajes y espacios
                const cleaned = exactMatch.replace(/,/g, '').replace(/%/g, '').replace(/\s/g, '');
                value = parseFloat(cleaned) || 0;
              } else {
                value = parseFloat(exactMatch) || 0;
              }
            } else {
              // Buscar case-insensitive
              const matchingKey = Object.keys(metricsData).find(k => 
                String(k).toLowerCase().trim() === fieldKeyLower
              );
              if (matchingKey) {
                const matchedValue = metricsData[matchingKey];
                if (typeof matchedValue === 'string') {
                  const cleaned = matchedValue.replace(/,/g, '').replace(/%/g, '').replace(/\s/g, '');
                  value = parseFloat(cleaned) || 0;
                } else {
                  value = parseFloat(matchedValue) || 0;
                }
              }
            }
          }
          
          return {
            name: label,
            fullName: label,
            value: value,
            level: 0,
            parent: null,
            key: fieldKey
          };
        })
        .filter(item => item.value > 0 || distributionProportionalConfig.length <= 3) // Mostrar todos si hay pocos campos
        .sort((a, b) => b.value - a.value);
      
      console.log('SubestadosHierarquicos: Datos de distribución proporcional desde campos del paso 4', {
        config: distributionProportionalConfig,
        datos: level0Filtered
      });
    } else if (distributionProportionalConfig && Array.isArray(distributionProportionalConfig) && distributionProportionalConfig.length === 0) {
      // Si la configuración está vacía pero existe, usar todos los campos disponibles de analyticsData
      const metricsData = analyticsData?.metrics || analyticsData?.m || {};
      if (metricsData && typeof metricsData === 'object') {
        level0Filtered = Object.keys(metricsData)
          .filter(key => {
            // Excluir campos que no son métricas numéricas
            const excludedKeys = ['calls_by_hours', 'calls_by_hours_answered', 'agents'];
            return !excludedKeys.includes(key);
          })
          .map(fieldKey => {
            const value = metricsData[fieldKey];
            const numValue = typeof value === 'string'
              ? parseFloat(value.replace(/,/g, '').replace(/%/g, '').replace(/\s/g, '')) || 0
              : parseFloat(value) || 0;
            
            // Obtener el label del campo (usar el configurado o convertir la key a formato legible)
            let label = fieldLabels?.[fieldKey];
            if (!label || label.trim() === '') {
              // Si no hay label configurado, convertir la key a formato legible
              label = String(fieldKey)
                .split('_')
                .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
            }
            
            return {
              name: label,
              fullName: label,
              value: numValue,
              level: 0,
              parent: null,
              key: fieldKey
            };
          })
          .filter(item => item.value > 0)
          .sort((a, b) => b.value - a.value);
      }
    } else {
      // Si no hay configuración, usar datos de nivel 0 de la jerarquía (comportamiento por defecto)
      level0Filtered = allData.filter(d => d.level === 0).sort((a, b) => b.value - a.value);
    }
    
    // Agrupar por nivel para diferentes visualizaciones
    const byLevel = {
      level0: level0Filtered, // Usar datos filtrados según configuración
      level1: level1Filtered, // Usar datos filtrados (sin "gente guía")
      level2: allData.filter(d => d.level === 2).sort((a, b) => b.value - a.value),
      hierarchical: hierarchicalData.sort((a, b) => b.rootValue - a.rootValue),
      all: allDataNoSum.sort((a, b) => b.value - a.value)
    };
    
    console.log('SubestadosHierarquicos chartData:', {
      level0: byLevel.level0.length,
      level1: byLevel.level1.length,
      level2: byLevel.level2.length,
      all: byLevel.all.length,
      sampleLevel1: byLevel.level1.slice(0, 3),
      sampleAll: byLevel.all.slice(0, 3),
      distributionProportional: byLevel.level0
    });
    
    return byLevel;
  }, [data, distributionProportionalConfig, analyticsData, fieldLabels, fieldFormats]);

  // Colores del tema: azul oscuro y naranja (PLANOS, sin degradados)
  const CHART_COLORS = [
    '#1356f1', '#f58319', '#0d2c6b', '#f59e0b',
    '#1356f1', '#f58319', '#0d2c6b', '#f59e0b',
    '#1356f1', '#f58319', '#0d2c6b', '#f59e0b'
  ];
  
  const getColorForLevel = (level, index) => {
    // Colores planos alternando entre azul oscuro y naranja
    if (level === 0) return '#1356f1'; // Azul oscuro principal (plano)
    if (level === 1) return '#f58319'; // Naranja principal (plano)
    if (level === 2) return '#1356f1'; // Azul oscuro para nivel 2 (plano)
    // Alternar entre azul y naranja para otros niveles
    return index % 2 === 0 ? '#1356f1' : '#f58319';
  };

  // Función mejorada para filtrar nodos recursivamente
  const shouldShowNode = (node, filterText) => {
    if (!filterText) return true;
    const filterLower = filterText.toLowerCase();
    
    // Si el nodo coincide con el filtro, mostrarlo
    if (node.label.toLowerCase().includes(filterLower)) return true;
    
    // Si tiene hijos, verificar si alguno coincide
    if (node.children && Object.keys(node.children).length > 0) {
      return Object.values(node.children).some(child => shouldShowNode(child, filterText));
    }
    
    return false;
  };

  const renderNode = (node, level = 0) => {
    const hasChildren = node.children && Object.keys(node.children).length > 0;
    const isExpanded = expandedNodes.has(node.key);
    const indent = level * 24;

    // Filtrar por texto usando la función mejorada
    if (filterText && !shouldShowNode(node, filterText)) {
      return null;
    }

    return (
      <div key={node.key} className="subestado-node">
        <div
          className={`subestado-row ${hasChildren ? 'has-children' : ''} level-${level}`}
          style={{ paddingLeft: `${indent + 12}px` }}
          onClick={() => hasChildren && toggleNode(node.key)}
        >
          <div className="subestado-content">
            {hasChildren && (
              <span className="expand-icon">
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            )}
            {!hasChildren && <span className="expand-icon-placeholder" />}
            <span className="subestado-label">{node.label}</span>
            <span className="subestado-count">{node.count?.toLocaleString() || 0}</span>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="subestado-children">
            {Object.values(node.children).map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="subestados-container">
        <div className="loading-state">
          <div className="spinner-enhanced"></div>
          <p>Cargando subestados...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="subestados-container">
        <div className="error-state">
          <p>Error al cargar subestados: {error}</p>
          <button onClick={fetchSubestados} className="retry-button">
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  // Verificar si hay datos o configuración
  const hasData = data?.data && Object.keys(data.data).length > 0;
  const hasConfig = data?.hierarchy && Array.isArray(data.hierarchy) && data.hierarchy.length > 0;
  
  if (!hasConfig) {
    return (
      <div className="subestados-container">
        <div className="empty-state">
          <p>No hay configuración de subestados disponible.</p>
          <p className="empty-hint">
            Configura la jerarquía de subestados en el panel de administración.
          </p>
        </div>
      </div>
    );
  }
  
  if (!hasData) {
    return (
      <div className="subestados-container">
        <div className="empty-state">
          <p>No hay datos disponibles para el rango de fechas seleccionado.</p>
          <p className="empty-hint">
            Selecciona un rango de fechas diferente o verifica que existan datos en la base de datos.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="subestados-container">
      {/* Header Mejorado */}
      <div className="subestados-header-enhanced">
        <div className="header-info-enhanced">
          <div className="title-wrapper">
            <BarChart3 size={28} className="title-icon" />
            <div>
              <h3 className="subestados-title-enhanced">Desglose de Subestados</h3>
              <p className="subestados-subtitle-enhanced">
                Análisis jerárquico de estados y subestados de llamadas
              </p>
            </div>
          </div>
        </div>
        
        {/* Controles Mejorados */}
        <div className="header-actions-enhanced">
          <div className="search-container-enhanced">
            <Search size={18} className="search-icon" />
            <input
              type="text"
              placeholder="Buscar por nombre..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="search-input-enhanced"
            />
            {filterText && (
              <button 
                onClick={() => setFilterText('')}
                className="clear-search-btn"
                title="Limpiar búsqueda"
              >
                ×
              </button>
            )}
          </div>
          
          <div className="action-buttons-group">
            <button onClick={expandAll} className="action-btn-enhanced primary">
              <ChevronDown size={16} />
              Expandir Todo
            </button>
            <button onClick={collapseAll} className="action-btn-enhanced secondary">
              <ChevronRight size={16} />
              Colapsar Todo
            </button>
          </div>
        </div>
      </div>

      {/* Estadísticas Mejoradas - Diseño Ultra Visual */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', 
        gap: '24px',
        marginBottom: '40px'
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, rgba(19, 86, 241, 0.05) 100%)',
          borderRadius: '20px',
          padding: '28px',
          border: '2px solid rgba(19, 86, 241, 0.2)',
          boxShadow: '0 8px 32px rgba(19, 86, 241, 0.15)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
          e.currentTarget.style.boxShadow = '0 16px 48px rgba(19, 86, 241, 0.25)';
          e.currentTarget.style.borderColor = 'rgba(19, 86, 241, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(19, 86, 241, 0.15)';
          e.currentTarget.style.borderColor = 'rgba(19, 86, 241, 0.2)';
        }}
        >
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(19, 86, 241, 0.1) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', position: 'relative', zIndex: 1 }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #1356f1 0%, #0d2c6b 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(19, 86, 241, 0.4)',
              position: 'relative'
            }}>
              <TrendingUp size={28} style={{ color: '#ffffff' }} />
              <div style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 100%)',
                pointerEvents: 'none'
              }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: 'rgba(13, 44, 107, 0.7)',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Total de Registros
              </div>
              <div style={{ 
                fontSize: '32px', 
                fontWeight: 900, 
                color: '#0d2c6b',
                fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
                lineHeight: 1.2,
                background: 'linear-gradient(135deg, #1356f1 0%, #0d2c6b 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {data.total_rows?.toLocaleString() || 0}
              </div>
            </div>
          </div>
        </div>
        
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, rgba(245, 131, 25, 0.05) 100%)',
          borderRadius: '20px',
          padding: '28px',
          border: '2px solid rgba(245, 131, 25, 0.2)',
          boxShadow: '0 8px 32px rgba(245, 131, 25, 0.15)',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          cursor: 'pointer'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = 'translateY(-8px) scale(1.02)';
          e.currentTarget.style.boxShadow = '0 16px 48px rgba(245, 131, 25, 0.25)';
          e.currentTarget.style.borderColor = 'rgba(245, 131, 25, 0.4)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'translateY(0) scale(1)';
          e.currentTarget.style.boxShadow = '0 8px 32px rgba(245, 131, 25, 0.15)';
          e.currentTarget.style.borderColor = 'rgba(245, 131, 25, 0.2)';
        }}
        >
          <div style={{
            position: 'absolute',
            top: '-50%',
            right: '-50%',
            width: '200%',
            height: '200%',
            background: 'radial-gradient(circle, rgba(245, 131, 25, 0.1) 0%, transparent 70%)',
            pointerEvents: 'none'
          }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px', position: 'relative', zIndex: 1 }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, #f58319 0%, #ff7b00 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(245, 131, 25, 0.4)',
              position: 'relative'
            }}>
              <Calendar size={28} style={{ color: '#ffffff' }} />
              <div style={{
                position: 'absolute',
                inset: 0,
                borderRadius: '16px',
                background: 'linear-gradient(135deg, rgba(255,255,255,0.3) 0%, transparent 100%)',
                pointerEvents: 'none'
              }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ 
                fontSize: '13px', 
                fontWeight: 600, 
                color: 'rgba(13, 44, 107, 0.7)',
                marginBottom: '8px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                Período de Análisis
              </div>
              <div style={{ 
                fontSize: '18px', 
                fontWeight: 800, 
                color: '#0d2c6b',
                lineHeight: 1.4
              }}>
                {data.date_range?.start_date || 'N/A'} - {data.date_range?.end_date || 'N/A'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* GRÁFICOS - DISEÑO ULTRA VISUAL E IMPACTANTE */}
      {chartData.level0 && chartData.level0.length > 0 && (
        <div style={{ 
          marginTop: '40px', 
          padding: '40px', 
          background: 'linear-gradient(135deg, #ffffff 0%, rgba(19, 86, 241, 0.02) 100%)', 
          borderRadius: '24px', 
          border: '2px solid rgba(13, 44, 107, 0.15)',
          boxShadow: '0 12px 48px rgba(13, 44, 107, 0.15)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Efecto de fondo decorativo */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #1356f1 0%, #f58319 100%)',
            borderRadius: '24px 24px 0 0'
          }} />
          
          <div style={{ 
            marginBottom: '40px', 
            paddingBottom: '24px', 
            borderBottom: '3px solid rgba(245, 131, 25, 0.2)',
            position: 'relative'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #1356f1 0%, #0d2c6b 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 8px 24px rgba(19, 86, 241, 0.3)'
              }}>
                <BarChart3 size={24} style={{ color: '#ffffff' }} />
              </div>
              <h4 style={{ 
                margin: 0, 
                fontSize: '26px', 
                fontWeight: 900,
                background: 'linear-gradient(135deg, #0d2c6b 0%, #1356f1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: '-0.5px'
              }}>
                Visualización Interactiva de Subestados
              </h4>
            </div>
            <p style={{ 
              margin: 0, 
              color: 'rgba(13, 44, 107, 0.7)', 
              fontSize: '15px',
              fontWeight: 500,
              paddingLeft: '64px'
            }}>
              Análisis completo de la distribución jerárquica con múltiples perspectivas
            </p>
          </div>
          
          {/* Gráfico 1: Distribución Proporcional - ULTRA VISUAL */}
          <div style={{ 
            marginBottom: '56px', 
            padding: '32px', 
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)', 
            borderRadius: '20px',
            border: '2px solid rgba(13, 44, 107, 0.12)',
            boxShadow: '0 8px 32px rgba(13, 44, 107, 0.12)',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.4s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '0 12px 48px rgba(13, 44, 107, 0.18)';
            e.currentTarget.style.borderColor = 'rgba(245, 131, 25, 0.3)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '0 8px 32px rgba(13, 44, 107, 0.12)';
            e.currentTarget.style.borderColor = 'rgba(13, 44, 107, 0.12)';
          }}
          >
            {/* Borde superior decorativo */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'linear-gradient(90deg, #1356f1 0%, #f58319 100%)',
              borderRadius: '20px 20px 0 0'
            }} />
            
            <h5 style={{ 
              marginBottom: '32px', 
              fontSize: '20px', 
              fontWeight: 800,
              paddingBottom: '16px',
              borderBottom: '3px solid rgba(245, 131, 25, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '6px',
                height: '24px',
                borderRadius: '3px',
                background: 'linear-gradient(180deg, #1356f1 0%, #f58319 100%)'
              }} />
              <span style={{
                background: 'linear-gradient(135deg, #0d2c6b 0%, #1356f1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Distribución Proporcional
              </span>
            </h5>
            
            <div style={{ 
              width: '100%', 
              height: '500px', 
              background: 'linear-gradient(135deg, #ffffff 0%, rgba(19, 86, 241, 0.02) 100%)', 
              padding: '32px', 
              borderRadius: '16px',
              border: '1px solid rgba(13, 44, 107, 0.08)',
              boxShadow: 'inset 0 2px 8px rgba(13, 44, 107, 0.05)',
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <defs>
                    <linearGradient id="pieGradientBlue" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#1356f1" stopOpacity={1} />
                      <stop offset="100%" stopColor="#0d2c6b" stopOpacity={1} />
                    </linearGradient>
                    <linearGradient id="pieGradientOrange" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#f58319" stopOpacity={1} />
                      <stop offset="100%" stopColor="#ff7b00" stopOpacity={1} />
                    </linearGradient>
                    <filter id="pieShadow">
                      <feDropShadow dx="0" dy="4" stdDeviation="8" floodColor="rgba(13, 44, 107, 0.3)"/>
                    </filter>
                  </defs>
                  <Pie
                    data={chartData.level0}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent, value }) => {
                      return `${name}\n${(percent * 100).toFixed(1)}%`;
                    }}
                    outerRadius={150}
                    innerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    paddingAngle={6}
                    animationBegin={0}
                    animationDuration={0}
                    isAnimationActive={false}
                  >
                    {chartData.level0.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={index % 2 === 0 ? "url(#pieGradientBlue)" : "url(#pieGradientOrange)"}
                        stroke="#ffffff"
                        strokeWidth={4}
                        style={{ 
                          filter: "url(#pieShadow)"
                        }}
                      />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              
              {/* Información permanente alrededor del gráfico */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                pointerEvents: 'none',
                zIndex: 10
              }}>
                <div style={{
                  fontSize: '16px',
                  fontWeight: 700,
                  color: 'rgba(13, 44, 107, 0.6)',
                  marginBottom: '8px'
                }}>
                  Total
                </div>
                <div style={{
                  fontSize: '36px',
                  fontWeight: 900,
                  color: '#0d2c6b',
                  fontFamily: 'ui-monospace, monospace',
                  marginBottom: '4px'
                }}>
                  {chartData.level0.reduce((sum, item) => sum + (item.value || 0), 0).toLocaleString()}
                </div>
                <div style={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'rgba(13, 44, 107, 0.5)'
                }}>
                  Registros
                </div>
              </div>
              
              {/* Recuadros informativos alrededor del gráfico */}
              {chartData.level0.map((entry, index) => {
                const total = chartData.level0.reduce((sum, item) => sum + (item.value || 0), 0);
                const percent = total > 0 ? ((entry.value / total) * 100).toFixed(1) : '0';
                const isBlue = index % 2 === 0;
                const angle = index === 0 ? -45 : 135; // Posicionamiento alrededor del círculo
                const radius = 200; // Distancia desde el centro
                const x = Math.cos(angle * Math.PI / 180) * radius;
                const y = Math.sin(angle * Math.PI / 180) * radius;
                
                return (
                  <div
                    key={`info-box-${index}`}
                    style={{
                      position: 'absolute',
                      top: `calc(50% + ${y}px)`,
                      left: `calc(50% + ${x}px)`,
                      transform: 'translate(-50%, -50%)',
                      background: 'rgba(255, 255, 255, 0.95)',
                      border: `2px solid ${isBlue ? 'rgba(19, 86, 241, 0.3)' : 'rgba(245, 131, 25, 0.3)'}`,
                      borderRadius: '12px',
                      padding: '16px 20px',
                      boxShadow: '0 4px 16px rgba(13, 44, 107, 0.15)',
                      minWidth: '180px',
                      textAlign: 'center',
                      pointerEvents: 'none',
                      zIndex: 5
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '4px',
                        background: isBlue ? '#1356f1' : '#f58319',
                        border: '2px solid #ffffff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }} />
                      <span style={{
                        color: 'rgba(13, 44, 107, 0.8)',
                        fontWeight: 700,
                        fontSize: '14px'
                      }}>
                        {entry.name}
                      </span>
                    </div>
                    <div style={{
                      fontSize: '24px',
                      fontWeight: 900,
                      color: 'rgba(13, 44, 107, 0.7)',
                      fontFamily: 'ui-monospace, monospace',
                      marginBottom: '4px'
                    }}>
                      {entry.value.toLocaleString()}
                    </div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: 700,
                      color: 'rgba(13, 44, 107, 0.5)'
                    }}>
                      {percent}%
                    </div>
                  </div>
                );
              })}
            </div>
          </div>


          {/* Gráfico 3: Top 20 Subestados - ULTRA VISUAL */}
          {chartData.all && chartData.all.length > 0 && (
            <div style={{ 
              marginBottom: '56px', 
              padding: '32px', 
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)', 
              borderRadius: '20px',
              border: '2px solid rgba(13, 44, 107, 0.12)',
              boxShadow: '0 8px 32px rgba(13, 44, 107, 0.12)',
              position: 'relative',
              overflow: 'hidden',
              transition: 'all 0.4s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 12px 48px rgba(13, 44, 107, 0.18)';
              e.currentTarget.style.borderColor = 'rgba(245, 131, 25, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(13, 44, 107, 0.12)';
              e.currentTarget.style.borderColor = 'rgba(13, 44, 107, 0.12)';
            }}
            >
              {/* Borde superior decorativo */}
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #1356f1 0%, #f58319 100%)',
                borderRadius: '20px 20px 0 0'
              }} />
              
              <h5 style={{ 
                marginBottom: '32px', 
                fontSize: '20px', 
                fontWeight: 800,
                paddingBottom: '16px',
                borderBottom: '3px solid rgba(245, 131, 25, 0.3)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '6px',
                  height: '24px',
                  borderRadius: '3px',
                  background: 'linear-gradient(180deg, #1356f1 0%, #f58319 100%)'
                }} />
                <span style={{
                  background: 'linear-gradient(135deg, #0d2c6b 0%, #1356f1 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Top 20 Subestados (Todos los Niveles)
                </span>
              </h5>
              
              <div style={{ 
                width: '100%', 
                minHeight: '600px',
                height: `${Math.max(700, chartData.all.slice(0, 20).length * 75 + 200)}px`,
                background: 'linear-gradient(135deg, #ffffff 0%, rgba(19, 86, 241, 0.02) 100%)', 
                padding: '32px', 
                borderRadius: '16px',
                border: '1px solid rgba(13, 44, 107, 0.08)',
                boxShadow: 'inset 0 2px 8px rgba(13, 44, 107, 0.05)',
                boxSizing: 'border-box'
              }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData.all.slice(0, 20)}
                    layout="vertical"
                    margin={{ top: 20, right: 160, left: 20, bottom: 80 }}
                  >
                    <defs>
                      <linearGradient id="top20GradientBlue" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#1356f1" stopOpacity={1} />
                        <stop offset="100%" stopColor="#0d2c6b" stopOpacity={1} />
                      </linearGradient>
                      <linearGradient id="top20GradientOrange" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#f58319" stopOpacity={1} />
                        <stop offset="100%" stopColor="#ff7b00" stopOpacity={1} />
                      </linearGradient>
                      <filter id="top20Shadow">
                        <feDropShadow dx="0" dy="3" stdDeviation="6" floodColor="rgba(13, 44, 107, 0.25)"/>
                      </filter>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(13, 44, 107, 0.1)" />
                    <XAxis 
                      type="number" 
                      stroke="#0d2c6b"
                      strokeWidth={2}
                      tick={{ fill: '#0d2c6b', fontSize: 13, fontWeight: 700 }}
                      tickFormatter={(value) => value.toLocaleString()}
                      axisLine={{ stroke: '#0d2c6b', strokeWidth: 2 }}
                    />
                    <YAxis 
                      type="category" 
                      dataKey="fullName" 
                      width={340}
                      stroke="#0d2c6b"
                      strokeWidth={2}
                      tick={{ fill: '#0d2c6b', fontSize: 12, fontWeight: 700 }}
                      interval={0}
                      axisLine={{ stroke: '#0d2c6b', strokeWidth: 2 }}
                      tickMargin={8}
                    />
                    <Tooltip
                      contentStyle={{
                        background: 'linear-gradient(135deg, #ffffff 0%, rgba(249, 250, 251, 1) 100%)',
                        border: '3px solid #f58319',
                        borderRadius: '16px',
                        padding: '16px',
                        boxShadow: '0 12px 40px rgba(13, 44, 107, 0.25)',
                        fontSize: '14px',
                        maxWidth: '450px',
                        wordBreak: 'break-word'
                      }}
                      labelStyle={{ 
                        color: '#0d2c6b', 
                        fontWeight: 800, 
                        fontSize: '16px',
                        marginBottom: '8px',
                        borderBottom: '2px solid rgba(245, 131, 25, 0.3)',
                        paddingBottom: '8px',
                        wordBreak: 'break-word'
                      }}
                      formatter={(value, name, props) => {
                        const total = chartData.all.slice(0, 20).reduce((sum, item) => sum + (item.value || 0), 0);
                        const percent = total > 0 ? ((value / total) * 100).toFixed(1) : '0';
                        return [
                          <div key="tooltip" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <span style={{ color: '#f58319', fontWeight: 800, fontSize: '22px' }}>
                              {value.toLocaleString()}
                            </span>
                            <span style={{ color: '#0d2c6b', fontWeight: 600, fontSize: '14px' }}>
                              {percent}% del total
                            </span>
                            <span style={{ 
                              color: 'rgba(13, 44, 107, 0.7)', 
                              fontSize: '12px', 
                              marginTop: '4px',
                              padding: '4px 8px',
                              background: 'rgba(13, 44, 107, 0.08)',
                              borderRadius: '6px',
                              display: 'inline-block'
                            }}>
                              Nivel: {props.payload.level + 1}
                            </span>
                          </div>
                        ];
                      }}
                      cursor={{ fill: 'rgba(245, 131, 25, 0.1)' }}
                    />
                    <Bar 
                      dataKey="value" 
                      radius={[0, 10, 10, 0]}
                      animationBegin={0}
                      animationDuration={1400}
                      animationEasing="ease-out"
                      label={{ 
                        position: 'right', 
                        fill: '#0d2c6b', 
                        fontSize: 12, 
                        fontWeight: 800,
                        formatter: (value) => value.toLocaleString(),
                        style: { textShadow: '0 1px 2px rgba(255, 255, 255, 0.8)' }
                      }}
                    >
                      {chartData.all.slice(0, 20).map((entry, index) => (
                        <Cell 
                          key={`top20-${index}`} 
                          fill={index % 2 === 0 ? "url(#top20GradientBlue)" : "url(#top20GradientOrange)"}
                          style={{ 
                            filter: "url(#top20Shadow)",
                            cursor: "pointer",
                            transition: "all 0.3s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.opacity = "0.85";
                            e.target.style.transform = "scaleY(1.05)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.opacity = "1";
                            e.target.style.transform = "scaleY(1)";
                          }}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Árbol Jerárquico */}
      <div className="subestados-tree-enhanced">
        <div className="tree-header">
          <h4 className="tree-title">Vista Detallada Jerárquica</h4>
          <p className="tree-subtitle">Explora la estructura completa de subestados</p>
        </div>
        <div className="tree-content">
          {Object.values(data.data).map(rootNode => renderNode(rootNode, 0))}
        </div>
      </div>
    </div>
  );
};

export default SubestadosHierarquicos;
